const NUMBER_OF_RETRIES = 2;

export default class PdfPageBase {
  pdf = null;
  pageId = null;
  renderTask = null;
  lastRendered = null;
  DOMContainer = null;
  pdfPage = null;
  canvas = null;
  apiManager = null;
  cancelRender = false;
  pageNumber = null;
  options = null;
  _pdfRequestCounter = 0;
  _pdfRequestLimit = 10;
  _loadingTask = null;
  _retrievePdfPromise = null;

  constructor(appState, apiManager, DOMContainer, pageId, pageNumber, options) {
    this.pageId = pageId;
    this.DOMContainer = DOMContainer;
    this.apiManager = apiManager;
    this.appState = appState;
    this.pageNumber = pageNumber;
    this.options = options;
    this.pdfPage = this.DOMContainer.querySelector(".page-pdf");
    this.appState.on(
      "appState:currentpagechanged",
      this.cleanupIfOutOfRange.bind(this)
    );
  }

  destroy() {
    this.pdf = null;
    this.renderTask = null;
    this.DOMContainer = null;
    this.pdfPage = null;
    this.canvas = null;
    this._loadingTask = null;
    this.appState.off(
      "appState:currentpagechanged",
      this.cleanupIfOutOfRange.bind(this)
    );
  }

  cleanupIfOutOfRange() {
    if (this.isOutOfRange()) {
      this._cleanup();
    }
  }

  _cleanup() {
    this.lastRendered = null;
    this.pdf = null;
    this._retrievePdfPromise = null;
    if (this._loadingTask) {
      this._loadingTask.destroy();
    }
  }

  async prefetchPDF() {
    await this._retrievePdf();
  }

  async getPdfUrl() {
    if (this.appState.newDownloaderFallbackMode) {
      const { userId, sessionId, downloadId } = this.appState;
      return this.apiManager.retrievePdfUrl(
        this.pageId,
        sessionId,
        userId,
        downloadId
      );
    }
    return this.appState.cdnAssetsDownloader.getPdfUrl(this.pageId);
  }

  async _retrievePdf(numberOfTries = 1) {
    if (!this._retrievePdfPromise) {
      this._retrievePdfPromise = new Promise(async (resolve) => {
        const url = await this.getPdfUrl();
        await this._loadPdfJs();
        await this.downloadPdfFromUrl(numberOfTries, url);
        resolve();
      });
    }
    await this._retrievePdfPromise;
  }

  async downloadPdfFromUrl(numberOfTries, url) {
    this._loadingTask = PDFJS.getDocument({ url });
    try {
      const pdfDocument = await this._loadingTask.promise;
      const page = await pdfDocument.getPage(1);
      this.pdf = page;
    } catch (error) {
      this._allowToRetrievePdf = true;
      await this._handlePdfDownloadError(error, numberOfTries);
      return error;
    }
  }

  async _handlePdfDownloadError(error, numberOfTries) {
    if (error.status == 403) {
      if (numberOfTries < NUMBER_OF_RETRIES) {
        await this.appState.cdnAssetsDownloader.requestDownloadCredentials();
        await this._retrievePdf(numberOfTries + 1);
      } else {
        this.appState.newDownloaderFallbackMode = true;
        await this._retrievePdf(numberOfTries + 1);
      }
    }
  }

  /**
   * Retrieves the zoom scale that needs to be used for the PDF page.
   * @returns {number}
   */
  getZoomScale() {
    const viewport = this.pdf.getViewport(1);
    const scaleX = this.DOMContainer.clientWidth / viewport.width;
    const scaleY = this.DOMContainer.clientHeight / viewport.height;
    const zoom = Math.min(scaleX, scaleY);
    return zoom;
  }

  /**
   * Returns true if the PDF page is considered to be out of range
   * and will not be needed immediately anymore.
   * @returns {boolean}
   */
  isOutOfRange() {
    return (
      this.pageNumber <
        this.appState.currentPage - this.options.pdfCleanupDelta ||
      this.pageNumber > this.appState.currentPage + this.options.pdfCleanupDelta
    );
  }

  /**
   * Loads a worker for rendering PDFs.
   */
  async _loadPdfJs() {}
}



// WEBPACK FOOTER //
// ./src/common/components/pdfPageBase.js